import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Lookup from '@/api/lookup/api-lookup';
import { Delete, Get, Put, Post } from '@/api/api-middleware';
import { getCompanyID, getEmployeeID } from '@/helpers/Utils';
import { firstListItem, itemPerPage } from '@/constants/defaultValues';
import { apiPath } from '@/constants/defaultValues';

const initialState = {
  loading: false,
  AddWorkScheduleResponse: [],
  PaginatedWorkScheduleResponse: [],
  WorkScheduleList: [],
  notification: { show: false, code: '' },
  WorkScheduleItem: {},
  AddUpdateDeleteAssignment: {},
  AddUpdateDeleteRequest: {},
  RemoteRequestWorkflow: {},
  RemoteWorkRequestsList: [],
  error: '',
};

export const GetAllRemoteWorkRequests = createAsyncThunk(
  'remoteRequest/GetAllRemoteWorkRequests',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/RemoteWorkRequest/GetAll'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchPaginatedWorkSchedule = createAsyncThunk(
  'workSchedule/fetchPaginatedWorkSchedule',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/WorkSchedule/GetAllPaginated'}`,
        data: {
          companyID: getCompanyID(),
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          keyWord: params.keyword,
        },
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getWorkScheduleById = createAsyncThunk(
  'workSchedule/getWorkScheduleById',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/WorkSchedule/GetByID/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const UpdateWorkSchedule = createAsyncThunk(
  'workSchedule/UpdateWorkSchedule',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/WorkSchedule/Update'}`,
        data: { ...params, companyID: getCompanyID() },
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteWorkScheduleById = createAsyncThunk(
  'workSchedule/deleteWorkScheduleById',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Lookup/WorkSchedule/Delete/${params}`,
      });
      thunkAPI.dispatch(
        fetchPaginatedWorkSchedule({
          pageNumber: 1,
          pageSize: itemPerPage,
          keyword: '',
        })
      );
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteRemoteWorkRequest = createAsyncThunk(
  'remoteRequest/deleteRemoteWorkRequest',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Operations/RemoteWorkRequest/Delete/${params.id}/${params.isFromhr}`,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const AddRemoteWorkRequest = createAsyncThunk(
  'remoteRequest/AddRemoteWorkRequest',
  async (params, thunkAPI) => {
    try {
      console.log(params?.id);

      // Determine if this is an update or add request
      const url = params?.id
        ? `${apiPath}api/Operations/RemoteWorkRequest/Update/${params.id.toString()}`
        : `${apiPath}api/Operations/RemoteWorkRequest/Add`;

      const method = params?.id ? Put : Post;

      // Make the request
      const response = await method({
        url,
        data: params,
      });
      // Handle successful response
      if (response?.data?.errCode === '200') {
        return response?.data?.data;
      }

      // Handle failure case
      return thunkAPI.rejectWithValue({
        errCode: response?.data?.errCode,
        message: response?.data?.errorMessage || 'Request failed.',
      });
    } catch (error) {
      // General error handling
      return thunkAPI.rejectWithValue({
        message: error?.message || 'An unexpected error occurred',
      });
    }
  }
);

export const GetRemoteRequestWorkflow = createAsyncThunk(
  'remoteRequest/GetRemoteRequestWorkflow',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/WorkFlow/GetWorkFlowRemoteWorkRequest'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const remoteRequestSlice = createSlice({
  name: 'remoteRequest',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.AddWorkScheduleResponse = [];
      state.PaginatedWorkScheduleResponse = [];
      state.WorkScheduleItem = {};
      state.loading = false;
      state.notification = { show: false, code: '' };
      state.AddUpdateDeleteAssignment = {};
      state.AddUpdateDeleteRequest = {};
      state.RemoteRequestWorkflow = {};
      state.RemoteWorkRequestsList = [];
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetAllRemoteWorkRequests.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(GetAllRemoteWorkRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.RemoteWorkRequestsList = action.payload;
        state.error = '';
      })
      .addCase(GetAllRemoteWorkRequests.rejected, (state, action) => {
        state.loading = false;
        state.RemoteWorkRequestsList = [];
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(deleteRemoteWorkRequest.pending, (state) => {
        state.loading = true;
        state.notification = { show: false };
        state.error = '';
      })
      .addCase(deleteRemoteWorkRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.notification =
          action?.payload?.data === '-2'
            ? { show: true, code: 'approvalExistsRequest' }
            : { show: true, code: action?.payload?.errCode };
        state.error = '';
      })
      .addCase(deleteRemoteWorkRequest.rejected, (state, action) => {
        state.loading = false;
        state.RemoteWorkRequestsList = [];
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(fetchPaginatedWorkSchedule.pending, (state) => {
        state.loading = true;
        state.WorkScheduleItem = {};
        state.AddWorkScheduleResponse = [];
        state.error = '';
        // state.notification = {show : false, code : ""};
      })
      .addCase(fetchPaginatedWorkSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.PaginatedWorkScheduleResponse = action.payload;
        state.error = '';
      })
      .addCase(fetchPaginatedWorkSchedule.rejected, (state, action) => {
        state.loading = false;
        state.PaginatedWorkScheduleResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getWorkScheduleById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.error = '';
      })
      .addCase(getWorkScheduleById.fulfilled, (state, action) => {
        state.loading = false;
        state.WorkScheduleItem = action.payload;
        state.error = '';
      })
      .addCase(getWorkScheduleById.rejected, (state, action) => {
        state.loading = false;
        state.WorkScheduleItem = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(UpdateWorkSchedule.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.AddWorkScheduleResponse = [];
        state.error = '';
      })
      .addCase(UpdateWorkSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.AddWorkScheduleResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(UpdateWorkSchedule.rejected, (state, action) => {
        state.loading = false;
        state.AddWorkScheduleResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(deleteWorkScheduleById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.error = '';
      })
      .addCase(deleteWorkScheduleById.fulfilled, (state, action) => {
        state.loading = false;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(deleteWorkScheduleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(AddRemoteWorkRequest.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.error = '';
      })
      .addCase(AddRemoteWorkRequest.fulfilled, (state, action) => {
        state.loading = false;

        //state.AddUpdateDeleteRequest = { status: true };
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(AddRemoteWorkRequest.rejected, (state, action) => {
        state.loading = false;
        //state.AddUpdateDeleteRequest = {};
        state.error = action.payload ?? 'An error occurred';
        state.notification = { show: false, code: '500' };
      });

    builder
      .addCase(GetRemoteRequestWorkflow.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.error = '';
      })
      .addCase(GetRemoteRequestWorkflow.fulfilled, (state, action) => {
        state.loading = false;
        state.RemoteRequestWorkflow = action?.payload;
        state.error = '';
      })
      .addCase(GetRemoteRequestWorkflow.rejected, (state, action) => {
        state.loading = false;
        state.RemoteRequestWorkflow = {};
        state.error = action.payload ?? 'An error occurred';
      });
  },
});
export const { clearCurrentItem } = remoteRequestSlice.actions;
export default remoteRequestSlice.reducer;
